import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  ArrowLeft, Plus, CheckCircle2, Clock, AlertCircle, Loader2,
  ChevronRight, Euro, FileText, CreditCard
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProjectDetailPage = () => {
  const { projectId } = useParams();
  const { user } = useAuth();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);

  const isCompany = project?.company_id === user?.id;
  const isContractor = project?.contractor_id === user?.id;

  const fetchProject = useCallback(async () => {
    try {
      const projRes = await axios.get(`${API}/projects/${projectId}`, { withCredentials: true });
      setProject(projRes.data);
    } catch (err) {
      toast.error('Erreur de chargement du projet');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    fetchProject();
  }, [fetchProject]);

  const updateMilestone = async (milestoneId, status) => {
    try {
      await axios.put(
        `${API}/projects/${projectId}/milestones/${milestoneId}`,
        { status },
        { withCredentials: true }
      );
      toast.success('Jalon mis à jour');
      fetchProject();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur de mise à jour');
    }
  };

  const getMilestoneStatusBadge = (status) => {
    const styles = {
      pending: 'bg-gray-100 text-gray-700',
      in_progress: 'bg-blue-100 text-blue-700',
      completed: 'bg-yellow-100 text-yellow-700',
      approved: 'bg-emerald-100 text-emerald-700'
    };
    const labels = {
      pending: 'En attente',
      in_progress: 'En cours',
      completed: 'Terminé',
      approved: 'Approuvé'
    };
    return <Badge className={styles[status] || styles.pending}>{labels[status] || status}</Badge>;
  };

  const getMilestoneIcon = (status) => {
    if (status === 'approved') return <CheckCircle2 className="w-5 h-5 text-emerald-500" />;
    if (status === 'completed') return <AlertCircle className="w-5 h-5 text-yellow-500" />;
    if (status === 'in_progress') return <Clock className="w-5 h-5 text-blue-500" />;
    return <Clock className="w-5 h-5 text-gray-400" />;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#1E3A8A]" />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-[#4B5563]">Projet non trouvé</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9FAFB] py-8" data-testid="project-detail-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/dashboard/projects" className="inline-flex items-center gap-2 text-[#1E3A8A] hover:underline mb-6">
          <ArrowLeft className="w-4 h-4" /> Retour aux projets
        </Link>

        {/* Header */}
        <Card className="mb-6">
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row justify-between gap-4">
              <div>
                <h1 className="font-heading font-bold text-2xl text-[#111827]" data-testid="project-title">
                  {project.job_title}
                </h1>
                <p className="text-[#4B5563] mt-1">
                  {isCompany ? `Sous-traitant: ${project.contractor_name}` : `Client: ${project.company_name}`}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <Badge className={project.status === 'completed' ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'}>
                  {project.status === 'completed' ? 'Terminé' : 'En cours'}
                </Badge>
              </div>
            </div>

            {/* Progress bar */}
            <div className="mt-6">
              <div className="flex justify-between text-sm mb-2">
                <span className="text-[#4B5563]">Progression</span>
                <span className="font-semibold text-[#111827]">{project.progress}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className="bg-[#1E3A8A] h-3 rounded-full transition-all duration-500"
                  style={{ width: `${project.progress}%` }}
                  data-testid="progress-bar"
                />
              </div>
            </div>

            {/* Budget summary */}
            <div className="grid grid-cols-3 gap-4 mt-6">
              <div className="text-center p-3 bg-[#F9FAFB] rounded-lg">
                <p className="text-xs text-[#4B5563]">Budget total</p>
                <p className="font-bold text-[#111827]">{project.total_budget?.toFixed(2) || '0.00'}€</p>
              </div>
              <div className="text-center p-3 bg-emerald-50 rounded-lg">
                <p className="text-xs text-[#4B5563]">{isCompany ? 'Payé' : 'Reçu'}</p>
                <p className="font-bold text-emerald-700">{project.paid_amount?.toFixed(2) || '0.00'}€</p>
              </div>
              <div className="text-center p-3 bg-yellow-50 rounded-lg">
                <p className="text-xs text-[#4B5563]">Restant</p>
                <p className="font-bold text-yellow-700">{((project.total_budget || 0) - (project.paid_amount || 0)).toFixed(2)}€</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Milestones */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#1E3A8A]" />
              Jalons du projet
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {project.milestones?.map((milestone, index) => {
                return (
                  <div
                    key={milestone.id}
                    className="border rounded-lg p-4 hover:border-[#1E3A8A]/30 transition-colors"
                    data-testid={`milestone-${milestone.id}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-3">
                        {getMilestoneIcon(milestone.status)}
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm text-[#6B7280]">Jalon {index + 1}</span>
                            {getMilestoneStatusBadge(milestone.status)}
                          </div>
                          <h4 className="font-medium text-[#111827]">{milestone.title}</h4>
                          {milestone.description && (
                            <p className="text-sm text-[#4B5563] mt-1">{milestone.description}</p>
                          )}
                          <div className="flex items-center gap-4 mt-2 text-sm text-[#6B7280]">
                            {milestone.amount && (
                              <span className="flex items-center gap-1">
                                <Euro className="w-3 h-3" /> {milestone.amount.toFixed(2)}€
                              </span>
                            )}
                            {milestone.due_date && (
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" /> {new Date(milestone.due_date).toLocaleDateString('fr-FR')}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex flex-col gap-2 flex-shrink-0">
                        {isContractor && milestone.status === 'pending' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => updateMilestone(milestone.id, 'in_progress')}
                            data-testid={`start-milestone-${milestone.id}`}
                          >
                            Démarrer
                          </Button>
                        )}
                        {isContractor && milestone.status === 'in_progress' && (
                          <Button
                            size="sm"
                            className="btn-primary"
                            onClick={() => updateMilestone(milestone.id, 'completed')}
                            data-testid={`complete-milestone-${milestone.id}`}
                          >
                            Marquer terminé
                          </Button>
                        )}
                        {isCompany && milestone.status === 'completed' && (
                          <Button
                            size="sm"
                            className="bg-emerald-600 hover:bg-emerald-700 text-white"
                            onClick={() => updateMilestone(milestone.id, 'approved')}
                            data-testid={`approve-milestone-${milestone.id}`}
                          >
                            <CheckCircle2 className="w-3 h-3 mr-1" /> Approuver
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Coordonnées de facturation */}
        {project.contractor_iban && (
          <Card>
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-[#1E3A8A]" />
                Coordonnées de facturation
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-[#4B5563] mb-3">Les paiements sont effectués directement entre les parties. Voici les coordonnées bancaires du sous-traitant :</p>
              <div className="p-4 bg-[#F9FAFB] rounded-lg">
                <p className="font-medium">{project.contractor_name}</p>
                <p className="text-sm font-mono text-[#1E3A8A] mt-1">{project.contractor_iban}</p>
              </div>
            </CardContent>
          </Card>
        )}

      </div>
    </div>
  );
};

export default ProjectDetailPage;
